import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Plus, Settings as SettingsIcon, Megaphone, Trash2, DollarSign, Info } from 'lucide-react';
import { formatDateTime, formatCurrency } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { RulesDrawer } from '../components/rules/RulesDrawer';

interface Campaign {
  id: number;
  slug: string;
  displayName: string;
  status: string;
  fallbackOfferId: number | null;
  botThreshold: number;
  rulesCount: number;
  createdAt: number;
}

interface TrackerDomain {
  id: number;
  hostname: string;
  status: string;
  isDefault: boolean;
}

export function CampaignsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState<Campaign | null>(null);
  const [costOpen, setCostOpen] = useState<Campaign | null>(null);

  const list = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiClient.get<Campaign[]>('/api/v1/campaigns'),
  });

  const offers = useQuery({
    queryKey: ['offers'],
    queryFn: () => apiClient.get<any[]>('/api/v1/offers'),
  });

  // Resolve the default active tracker domain dynamically. Falls back to a
  // placeholder if no domain is registered yet (admin hasn't completed setup).
  // This replaces a hardcoded `tracker.rjnetwork.site` reference that stayed
  // stale after the user pointed traffic at `trk.nguwelitan.online`.
  const domains = useQuery({
    queryKey: ['tracker-domains-default'],
    queryFn: () => apiClient.get<TrackerDomain[]>('/api/v1/tracker-domains'),
    staleTime: 5 * 60_000,
  });
  const defaultDomain =
    (domains.data ?? []).find((d) => d.isDefault && d.status !== 'disabled')?.hostname
    ?? (domains.data ?? []).find((d) => d.status === 'active')?.hostname
    ?? 'your-tracker-domain.com';

  const create = useMutation({
    mutationFn: (data: { slug: string; displayName: string }) =>
      apiClient.post('/api/v1/campaigns', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setCreateOpen(false);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      apiClient.delete(`/api/v1/campaigns/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const handleArchiveOrDelete = async (camp: { id: number; displayName: string }) => {
    const result = await confirm({
      title: `Delete ${camp.displayName}?`,
      description: 'This permanently removes the campaign + all routing rules. Past clicks keep their campaign_id but lose the slug.',
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> }],
    });
    if (result === 'delete') archive.mutate({ id: camp.id, hard: true });
  };

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      <SectionHeader
        title="Campaigns"
        subtitle={`${list.data?.length ?? 0} deployment units`}
        actions={
          <Button icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Campaign
          </Button>
        }
      />

      {/* Hint banner: tell user where the actual usable links live, since
          we removed the per-campaign tracking URL column. Without this,
          someone landing here for the first time would wonder how to get
          a shareable link. */}
      <div className="cyber-panel p-3.5 border-neon-cyan/30 bg-neon-cyan/5 text-[11px] font-mono text-text-secondary leading-relaxed flex gap-2.5 items-start">
        <Info size={14} className="inline shrink-0 text-neon-cyan mt-0.5" />
        <div>
          Tracking URL aktual = <code className="text-neon-cyan">https://{defaultDomain}/&lt;slug&gt;?sub_id=&lt;sub_id&gt;</code>.
          Untuk dapat link siap-share dengan sub_id otomatis, buka{' '}
          <a href="/publishers" className="text-neon-cyan hover:underline">/publishers</a>{' '}→ pilih sub-publisher → klik tombol <strong>Get Link</strong>.
        </div>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Rules</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="font-display font-bold text-sm">{c.displayName}</div>
                    <div className="text-[11px] text-text-tertiary font-mono">slug: {c.slug}</div>
                  </td>
                  <td className="font-mono text-xs">
                    <button
                      onClick={() => setRulesOpen(c)}
                      className="text-neon-cyan hover:underline"
                    >
                      {c.rulesCount} rule{c.rulesCount !== 1 ? 's' : ''}
                    </button>
                  </td>
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="text-xs text-text-tertiary font-mono">{formatDateTime(c.createdAt)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      <Button variant="ghost" size="sm" icon={<SettingsIcon size={12} />} onClick={() => setRulesOpen(c)}>
                        Rules
                      </Button>
                      <Button variant="ghost" size="sm" icon={<DollarSign size={12} />} onClick={() => setCostOpen(c)}>
                        Cost
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={12} />}
                        onClick={() => handleArchiveOrDelete(c)}
                        title="Archive or delete"
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-text-tertiary">
                    <Megaphone className="mx-auto mb-2" size={32} />
                    <p className="font-mono text-xs">no campaigns yet — create one to start tracking</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Modal */}
      <CreateCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(data) => create.mutate(data)}
        loading={create.isPending}
        defaultDomain={defaultDomain}
      />

      {/* Rules Drawer */}
      {rulesOpen && (
        <RulesDrawer campaign={rulesOpen} onClose={() => setRulesOpen(null)} offers={offers.data ?? []} />
      )}

      {/* Daily ad-cost editor (P0-A) */}
      {costOpen && (
        <CostModal campaign={costOpen} onClose={() => setCostOpen(null)} />
      )}
    </div>
  );
}

function CreateCampaignModal({
  open,
  onClose,
  onSubmit,
  loading,
  defaultDomain,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { slug: string; displayName: string }) => void;
  loading: boolean;
  defaultDomain: string;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="New Tracking Unit" description="Define your campaign identity">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ slug, displayName: name });
        }}
        className="w-full space-y-4"
      >
        <Input
          label="Campaign Slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
          placeholder="my-campaign"
          hint={`Forms your tracking URL: ${defaultDomain}/{slug}`}
          required
        />
        <Input
          label="Display Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Black Friday Promo"
          required
        />
        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading} icon={<Plus size={14} />}>Initialize Unit</Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── CostModal — input biaya iklan harian (P0-A, model 'manual') ────────────
// Satu baris per hari UTC; submit ulang tanggal sama = timpa (idempoten).
function CostModal({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [cost, setCost] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['campaign-costs', campaign.id],
    queryFn: () =>
      apiClient.get<Array<{ id: number; date: string; cost: number; note: string | null }>>(
        `/api/v1/campaigns/${campaign.id}/costs`,
      ),
  });

  const save = useMutation({
    mutationFn: () =>
      apiClient.put(`/api/v1/campaigns/${campaign.id}/costs`, {
        date,
        cost: Number(cost),
        note: note || undefined,
      }),
    onSuccess: () => {
      setCost('');
      setNote('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['campaign-costs', campaign.id] });
      // Overview/by-campaign cache membawa adSpend — invalidasi agar ROI
      // langsung bergerak tanpa menunggu TTL.
      qc.invalidateQueries({ queryKey: ['stats-overview'] });
      qc.invalidateQueries({ queryKey: ['stats-by-campaign'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const remove = useMutation({
    mutationFn: (costId: number) =>
      apiClient.delete(`/api/v1/campaigns/${campaign.id}/costs/${costId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-costs', campaign.id] }),
  });

  return (
    <Modal open onClose={onClose} title={`Daily Ad Cost: ${campaign.displayName}`} size="md">
      <form
        className="w-full space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!date || !(Number(cost) >= 0)) {
            setError('Tanggal dan cost wajib diisi');
            return;
          }
          save.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
              Tanggal (UTC)
            </span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-cyber h-9 w-full"
              required
            />
          </label>
          <Input
            label="Cost (USD)"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="0.00"
            type="number"
            min="0"
            step="0.01"
            required
          />
        </div>
        <Input label="Catatan" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. deposit FB Ads" />
        {error && <p className="text-xs font-mono text-neon-red">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="submit" size="sm" loading={save.isPending} icon={<Plus size={14} />}>
            Simpan
          </Button>
        </div>
      </form>

      <div className="mt-5 border-t border-border-subtle pt-3">
        <h4 className="font-display text-[11px] uppercase tracking-wider text-neon-cyan mb-2">
          Riwayat
        </h4>
        <div className="max-h-56 overflow-y-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Tanggal</th>
                <th className="text-right">Cost</th>
                <th>Catatan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="font-mono text-xs">{row.date}</td>
                  <td className="text-right numeric text-neon-orange">{formatCurrency(row.cost)}</td>
                  <td className="text-[11px] text-text-tertiary truncate max-w-[10rem]">{row.note ?? '—'}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" icon={<Trash2 size={12} />} onClick={() => remove.mutate(row.id)} />
                  </td>
                </tr>
              ))}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={4} className="text-center py-6 font-mono text-xs text-text-tertiary">
                    belum ada biaya tercatat
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] font-mono text-text-tertiary mt-2">
          ⓘ Model cost kampanye diatur via API (cost_model=cpc/cpm/manual); tabel ini untuk model manual. Kolom Cost/ROI tampil di Overviews &amp; Statistics (admin).
        </p>
      </div>
    </Modal>
  );
}
